import { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import type { MarketingLocale } from './guides';
import { productVideo } from './productVideoContent';

type Props = { locale: MarketingLocale; onStarted?: () => void; onCompleted?: () => void };

function Player({ locale, onStarted, onCompleted }: Props) {
  const copy = productVideo(locale);
  const video = useRef<HTMLVideoElement>(null);
  const started = useRef(false), completed = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  async function play() {
    setLoading(true); setError(false);
    try { await video.current?.play(); }
    catch { setLoading(false); setError(true); }
  }
  return <figure className="ms-film">
    <div className="ms-film-frame">
      <video ref={video} src={copy.src} poster={copy.poster} preload="none" controls playsInline
        width="1920" height="1080" aria-label={copy.watch}
        onPlaying={() => { setPlaying(true); setLoading(false); setError(false); if (!started.current) { started.current = true; onStarted?.(); } }}
        onEnded={() => { if (!completed.current) { completed.current = true; onCompleted?.(); } }}
        onError={() => { setError(true); setLoading(false); }}>
        <a href={copy.src}>{copy.open}</a>
      </video>
      {!playing && <button type="button" className="ms-film-play" onClick={() => void play()} disabled={loading} aria-label={copy.play}>
        <Play size={23} aria-hidden="true" fill="currentColor" /><span>{loading ? copy.loading : copy.watch}</span>
      </button>}
    </div>
    <figcaption className="ms-fine">{copy.note}</figcaption>
    {error && <p className="ms-film-error" role="status">{copy.error} <a href={copy.src}>{copy.open} ↗</a></p>}
  </figure>;
}

export default function ProductVideo(props: Props) {
  return <Player key={props.locale} {...props} />;
}

export function VideoWalkthrough({ locale }: { locale: MarketingLocale }) {
  const copy = productVideo(locale);
  return <details className="ms-film-transcript"><summary>{copy.transcript}</summary><ol>
    {copy.steps.map(([time, title, body]) => <li key={time}><span className="ms-film-time">{time}</span><div><h3>{title}</h3><p>{body}</p></div></li>)}
  </ol></details>;
}
